/**
 * faceCaptureSave — from a finished take to a face animation ASSET (ENG-1107).
 *
 * Exactly the path the content browser's FaceAnimWizard takes for a dropped
 * face JSON: `CustomAssetManager.uploadAnimationAsset(file, { kind: 'face',
 * durationMs })`, which POSTs `/api/characters/{slug}/animation-assets` and
 * hot-registers the clip. The clip is ALSO registered locally first
 * (`faceClipPlayer.loadCustomClip`) so the user can preview the take at once;
 * the server row is the source of truth, the local registration is the
 * preview. An upload failure is thrown to the caller — the pill renders it as
 * a DirtyBadge error with the real message; nothing is written to
 * localStorage (a recording is not a draft of a server resource, it is a new
 * file the user either saves or discards).
 *
 * Both collaborators are injectable so node:test can drive this without the
 * asset manager or the player singletons; the defaults are lazy imports so
 * this module itself imports cleanly under node:test.
 */
const pad = (n) => String(n).padStart(2, '0')

export function captureDefaultName(date = new Date()) {
  return `capture_${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}`
}

/** Same rule as FaceAnimWizard's baseName(): strip an extension, keep [A-Za-z0-9_-]. */
export function sanitizeClipName(s) {
  return String(s ?? '').trim().replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9_\-]+/g, '_').replace(/^_+|_+$/g, '')
}

async function defaultPlayer() {
  const { faceClipPlayer } = await import('./FaceClipPlayer.js')
  return faceClipPlayer
}

async function defaultUploader(file, opts) {
  const { CustomAssetManager } = await import('./CustomAssetManager.js')
  return CustomAssetManager.uploadAnimationAsset(file, opts)
}

/**
 * @param {object} p
 * @param {string} p.name
 * @param {{fps:number, frames:Array<{timeCode:number}>}} p.clip
 * @param {{loadCustomClip:(name:string, clip:object)=>void}} [p.player]
 * @param {(file:File, opts:object)=>Promise<object>} [p.uploader]
 * @returns {Promise<object>} the animation_assets row
 */
export async function saveFaceCapture({ name, clip, player = null, uploader = defaultUploader }) {
  const clean = sanitizeClipName(name)
  if (!clean) throw new Error('A name is required')
  if (!clip?.frames?.length) throw new Error('The take has no frames')
  const p = player ?? (await defaultPlayer())
  p.loadCustomClip(clean, clip)
  const durationMs = Math.round(clip.frames[clip.frames.length - 1].timeCode * 1000)
  const file = new File([JSON.stringify(clip)], `${clean}.json`, { type: 'application/json' })
  return uploader(file, { name: clean, kind: 'face', category: 'other', durationMs })
}
